import { Feature } from '@/pinia/feature'

const colorScale = {
  type: 'INTERPOLATE',
  cutoffs: [
    {
      value: 0,
      color: '#FF4D4F'
    },
    {
      value: 0.5,
      color: '#FFA940'
    },
    {
      value: 1,
      color: '#73D13D'
    }
  ]
}

const formatPercent = {
  format: {
    type: 'intl-number',
    style: 'percent'
  }
}

const colorLegend: Feature = {
  name: 'color-legend',
  enabled: true,
  title: {
    en: 'Indoor climate index',
    sv: 'Inomhusklimatindex'
  },
  component: 'ColorLegend',
  props: {
    type: 'continuous',
    colorScale,
    format: formatPercent
  }
}

export const climateIndex: Feature = {
  name: 'index',
  enabled: false,
  title: {
    en: 'Indoor climate index',
    sv: 'Inomhusklimatindex'
  },
  info: {
    en: 'Indoor climate index is visualized in Colored Buildings and Colored Rooms. Indoor climate index gives an overview of the quality of the indoor climate.',
    sv: 'Inomhusklimatindex visualiseras i Colored Buildings och Colored Rooms. Inomhusklimatindex ger en överblick över kvalitén på inomhusklimatet.'
  },
  isRoute: true,
  component: 'DashboardDataSectionFlat',
  props: {
    dataSources: [
      {
        id: 'climate.index.value',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.index.kpi',
        path: 'kpis[0].values[0].value'
      },
      {
        id: 'climate.index.colored-entities',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.index.kpi',
        transform: {
          id: 'climate.index.colored-entities',
          index: 1
        }
      },
      {
        id: 'climate.index.detail',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.index.detail',
        transform: {
          id: 'climate.index.detail',
          format: formatPercent,
          detailRowNames: {
            ICI_TEMPERATURE_STABILITY: {
              sv: 'Temperaturstabilitet',
              en: 'Temperature stability'
            },
            ICI_TEMPERATURE_COMFORT_COOL: {
              sv: 'Komforttemperatur, svalt',
              en: 'Comfort temperature, cool'
            },
            ICI_TEMPERATURE_COMFORT_WARM: {
              sv: 'Komforttemperatur, varmt',
              en: 'Comfort temperature, warm'
            },
            ICI_CO2: {
              sv: 'CO2-nivå',
              en: 'CO2 level'
            }
          }
        }
      },
      {
        id: 'climate.index.heatmap',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.index.heatmap'
      }
    ]
  },
  children: [
    {
      name: 'index',
      enabled: true,
      title: {
        en: 'Mean value',
        sv: 'Medelvärde'
      },
      component: 'MainMeasurementCard',
      info: {
        en: 'The index is comprised of four parts that assess whether the rooms are too hot or cold, maintain a steady temperature throughout the day, and have a good CO2 level. A Climate Index of 100% means that all parts have performed according to expectations. The Indoor Climate Index is based on the sum of individual component values for rooms and is aggregated depending on the selection in time and level; room, floor, building, and campus.  \n\n***Comfort Temperature, Cool***:  \nFor each hour the temperature exceeds 23C, 1/9 is subtracted from the value. If the temperature at any time exceeds 27C, the entire component value is set to 0 for that day.  \n\n***Comfort Temperature, Warm***:  \nFor each hour the temperature is below 20C, 1/9 is subtracted from the value. If the temperature at any time falls below 18C, the entire component value is set to 0 for that day.  \n\n***Temperature Stability***:  \nFor each hour the temperature is more than 1 degree from the average temperature, 1/9 is subtracted from the value.  \n\n***CO2 Level***:  \nFor each hour the CO2 level exceeds 800 ppm, 1/9 is subtracted from the value. If the CO2 level at any time exceeds 1000 ppm, the component is set to 0. Rooms without CO2 measurement automatically receive 100% on this component.',
        sv: 'Indexet är uppbyggt av fyra delar som bedömer om det är för varmt eller kallt i rummen, rummen håller jämn temperatur under dagen och har en bra CO2 nivå. Klimatindex 100% innebär att alla delar levererat enligt förväntan.  Inomhusklimatindex baseras på summan av rummens individuella komponentvärden och aggregeras beroende på urval i tid och nivå; rum, våningsplan, byggnad och campus.  \n\n***Komforttemperatur, svalt***:  \n För varje timme temperaturen överstiger 23C subtraheras 1/9 från värdet. Om temperaturen någon gång överstiger 27C sätts hela komponent värdet till 0 den dagen.  \n\n***Komforttemperatur, varmt***:  \n För varje timme temperaturen understiger 20C subtraheras 1/9 från värdet. Om temperaturen någon gång understiger 18C sätts hela komponent värdet till 0 den dagen.  \n\n***Temperaturstabilitet***:  \n För varje timme som temperaturen ligger mer än 1 grad ifrån medeltemperaturen så subtraheras 1/9 från värdet.  \n\n***CO2 nivå***:  \n För varje timme som CO2-nivån överstiger 800 ppm subtraheras 1/9 från värdet. Om CO2-nivån någon gång överstiger 1000 ppm så sätts komponenten till 0. Rum som inte har CO2 mätning får automatiskt 100% på denna komponent.'
      },
      props: {
        initialShowOnMap: true,
        colorScale,
        value: {
          type: 'gauge',
          dataSource: 'climate.index.value',
          format: formatPercent
        },
        coloredEntities: {
          dataSource: `climate.index.colored-entities`
        },
        table: {
          dataSource: 'climate.index.detail',
          headers: [
            {
              sv: 'Värde',
              en: 'Value'
            },
            {
              sv: 'Utfall',
              en: 'Outcome'
            }
          ]
        }
      },
      children: [colorLegend]
    },
    {
      name: 'index-heatmap',
      enabled: true,
      component: 'CardHeatmap',
      title: {
        en: '',
        sv: ''
      },
      info: {
        en: 'The color chart shows the indoor climate index per day for rooms based on the selected sample.  \n\n**Room per day:**  \nAverage of the components.  \n\n**Total per room:**  \nAverage for 2/3 of the lowest days for room/day. This is done with the intention of giving greater weight to remaining/recurring problems in the room.  \n\n**Total:**  \nAverage of the total per room. This corresponds to the total indoor climate index for the selected sample',
        sv: 'Färgdiagrammet visar rummens inomhusklimatindex per dag för valt urval.  \n\n**Rum per dag:**  \nGenomsnitt av komponenterna.  \n\n**Summa per rum:**  \nGenomsnitt för 2/3 av lägsta dagarna för rum/dag. Detta görs i syfte att ge större vikt åt kvarvarande/återkommande problem på rum.  \n\n**Total:**  \nGenomsnitt av summan per rum. Detta motsvarar totala inomhusklimatindexet för valt urval'
      },
      props: {
        titles: {
          CONSTRUCTION: {
            en: 'Overview construction per day',
            sv: 'Översikt byggnad per dag'
          },
          LEVEL: {
            en: 'Overview level per day',
            sv: 'Översikt våning per dag'
          },
          SPACE: {
            en: 'Overview space per day',
            sv: 'Översikt rum per dag'
          },
          SUB_MEASUREMENT: {
            en: 'Overview measurement per day',
            sv: 'Översikt mätvärde per dag'
          }
        },
        dataSource: 'climate.index.heatmap',
        colorScale,
        xSumLabel: {
          en: '',
          sv: ''
        },
        ySumLabel: {
          en: '',
          sv: ''
        },
        tooltipLabel: {
          en: 'Climate index',
          sv: 'Klimatindex'
        },
        dataFormat: formatPercent,
        timeFormat: {
          format: { type: 'intl-datetime', day: 'numeric', month: 'short' }
        },
        enabledEntityLevels: ['COMPLEX', 'CONSTRUCTION', 'LEVEL', 'FLOOR', 'SPACE'],
        errorMessages: {
          NO_DATA: {
            en: 'No data available',
            sv: 'Ingen data tillgänglig'
          },
          INVALID_FILTER: {
            en: 'Filter on floor to show data',
            sv: 'Filtrera på våning för att visa data'
          }
        },
        subMeasurementToTitle: {
          ICI_TEMPERATURE_STABILITY: {
            en: 'Temperature stability',
            sv: 'Temperaturstabilitet'
          },
          ICI_TEMPERATURE_COMFORT_COOL: {
            en: 'Comfort temperature, cool',
            sv: 'Komforttemperatur, svalt'
          },
          ICI_TEMPERATURE_COMFORT_WARM: {
            en: 'Comfort temperature, warm',
            sv: 'Komforttemperatur, varmt'
          },
          ICI_CO2: {
            en: 'CO2 level',
            sv: 'CO2-nivå'
          }
        }
      }
    }
  ]
}
